import React, { useState } from 'react';
import { Play, Radio, MessageCircle, ExternalLink, CalendarClock } from 'lucide-react';
import { SITE, SOCIALS, STREAM_SCHEDULE } from '../mock';
import { nextStream, fmtLocal, REF_TZ } from '../utils/schedule';
import { CopyCode } from './CopyCode';
import { useReveal } from '../hooks/useReveal';

const KickBtn = ({ testId, small }) => (
  <a href={SOCIALS.kick} target="_blank" rel="noopener noreferrer" data-testid={testId}
    className={`btn-cyan ${small ? '!py-2.5 !px-4 text-xs' : ''}`} style={{ background: '#53fc18', color: '#06130a' }}>
    <Play size={16} aria-hidden="true" /> WATCH ON KICK
  </a>
);

const StatusPill = ({ status }) => {
  if (status === 'live') return <span data-testid="stream-pill" className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-display font-bold tracking-wider" style={{ background: 'rgba(34,197,94,0.18)', color: '#4ADE80', border: '1px solid rgba(74,222,128,0.5)' }}><span className="w-2 h-2 rounded-full bg-[#22c55e] ar-pulse" aria-hidden="true" /> LIVE NOW</span>;
  if (status === 'offline') return <span data-testid="stream-pill" className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-display font-bold tracking-wider text-muted" style={{ background: 'rgba(8,11,18,0.75)', border: '1px solid var(--ar-border)' }}><span className="w-2 h-2 rounded-full bg-slate-500" aria-hidden="true" /> OFFLINE</span>;
  return null;
};

function StreamCard({ s }) {
  const [player, setPlayer] = useState(false);
  const live = s.status === 'live';
  const img = live && s.thumbnail ? s.thumbnail : '/assets/risky-campfire.jpg';
  return (
    <div data-testid="stream-main-card" className="relative rounded-2xl overflow-hidden min-h-[380px] lg:min-h-[480px] lg:col-span-3 border border-[var(--ar-border)] flex flex-col justify-end" style={{ background: '#0b111d' }}>
      {player && live ? (
        <iframe title="AsianRisk live on Kick" src="https://player.kick.com/asianrisk?autoplay=false&muted=true" className="absolute inset-0 w-full h-full" allowFullScreen data-testid="stream-embed" />
      ) : (
        <>
          <img src={img} alt={live ? 'Current AsianRisk stream preview' : 'Risky, the AsianRisk mascot, by a campfire'} className="absolute inset-0 w-full h-full object-cover object-[70%_center]" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(8,11,18,0.95) 0%, rgba(8,11,18,0.7) 45%, rgba(8,11,18,0.15) 100%), linear-gradient(0deg, rgba(8,11,18,0.9) 0%, transparent 55%)' }} />
          <div className="absolute top-5 left-5"><StatusPill status={s.status} /></div>
          <div className="relative p-6 sm:p-9 max-w-lg">
            <h2 className="ar-heading text-3xl sm:text-4xl mb-3">Watch AsianRisk <span className="text-cyan">Live</span></h2>
            <p className="text-muted mb-6">Live Roobet streams, chat reactions, and community moments.</p>
            <div className="flex flex-wrap gap-3">
              <KickBtn testId="stream-watch-kick-btn" />
              {live && <button type="button" onClick={() => setPlayer(true)} data-testid="stream-load-player-btn" className="btn-gold"><Radio size={16} aria-hidden="true" /> WATCH HERE (MUTED)</button>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatusCard({ s }) {
  const next = s.status === 'offline' && STREAM_SCHEDULE.length ? nextStream(STREAM_SCHEDULE) : null;
  return (
    <div data-testid="stream-status-card" className="ar-panel p-5 flex flex-col">
      <span className="ar-eyebrow mb-3">Stream Status</span>
      {s.status === 'loading' && <div className="space-y-2"><div className="ar-skel h-5 w-24" /><div className="ar-skel h-4 w-full" /></div>}
      {s.status === 'live' && (
        <>
          <span className="flex items-center gap-2 font-display font-bold text-[#4ADE80]"><span className="w-2.5 h-2.5 rounded-full bg-[#22c55e] ar-pulse" aria-hidden="true" /> LIVE NOW</span>
          {s.title && <p data-testid="stream-title" className="text-sm text-[var(--ar-text)] mt-2 line-clamp-3 break-words">{s.title}</p>}
        </>
      )}
      {s.status === 'offline' && (
        <>
          <span data-testid="stream-offline-text" className="font-display font-bold">Currently offline</span>
          {next && (
            <div data-testid="stream-next" className="mt-2 text-xs text-muted flex gap-2">
              <CalendarClock size={14} className="text-cyan flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span>Next stream: <span className="text-[var(--ar-text)]">{fmtLocal(next)}</span><br />Amsterdam: {fmtLocal(next, REF_TZ)}</span>
            </div>
          )}
        </>
      )}
      {s.status === 'unknown' && <p className="text-sm text-muted">Check the channel for the latest.</p>}
      {s.status !== 'loading' && <div className="mt-auto pt-4"><a href={SOCIALS.kick} target="_blank" rel="noopener noreferrer" data-testid="stream-status-kick-link" className="inline-flex items-center gap-1.5 text-xs font-display font-bold text-cyan hover:underline">WATCH ON KICK <ExternalLink size={12} aria-hidden="true" /></a></div>}
    </div>
  );
}

const CodeCard = () => (
  <div data-testid="stream-code-card" className="ar-panel p-5 flex flex-col">
    <span className="ar-eyebrow mb-3">Referral Code</span>
    <span className="font-display font-bold text-xs text-muted">CODE</span>
    <span className="font-display font-extrabold text-2xl text-cyan tracking-wide mb-4">{SITE.code}</span>
    <div className="mt-auto"><CopyCode testId="stream-copy-code-btn" /></div>
  </div>
);

const CommunityCard = () => (
  <div data-testid="stream-community-card" className="ar-panel p-6 flex flex-col gap-4">
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(88,101,242,0.15)', border: '1px solid rgba(88,101,242,0.45)' }}>
        <MessageCircle size={24} style={{ color: '#7c86ff' }} aria-hidden="true" />
      </div>
      <h3 className="font-display font-bold text-lg">Community Events &amp; Giveaways</h3>
    </div>
    <p className="text-sm text-muted">Events and giveaways are announced in the Discord, each with its own rules and details.</p>
    <a href={SOCIALS.discord} target="_blank" rel="noopener noreferrer" data-testid="stream-join-discord-btn" className="btn-gold !py-3 self-start">JOIN DISCORD</a>
  </div>
);

export default function LiveSection({ stream: s }) {
  useReveal();
  return (
    <section id="live" className="py-12 sm:py-16 relative">
      <div className="ar-container grid lg:grid-cols-5 gap-5 reveal">
        <StreamCard s={s} />
        <div className="lg:col-span-2 flex flex-col gap-5">
          <CommunityCard />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 flex-1">
            <StatusCard s={s} />
            <CodeCard />
          </div>
        </div>
      </div>
    </section>
  );
}
